import { useEffect, useMemo } from 'react';
import map from 'lodash/map';
import { useSelector } from 'react-redux';
import { EDP_TYPE, PDP_ACCESSORIES_TYPE, PDP_CLOTHING_TYPE, PDP_TYPE, TEST_RIDE_CLP_TYPE, TEST_RIDE_PLP_TYPE, } from '@yme/common/constants';
import { COLLECTION_PRODUCT_ATTR } from '@yme/common/selectors';
import { getUrlWithTestRideMode } from '@yme/organisms/test-ride-grid/helpers';
import { useQueryParams } from '@yme/organisms/test-ride-grid/useQueryParams';
import { getKeyForGroupingProduct, getLinkForTestRide } from '@yme/pdp-components/ProductCard';
import { getCommon, getPageLevelNavigationData } from '@yme/selectors/common';
import { getAttributeByName } from '@yme/selectors/helpers';
import { getProductDetails } from '@yme/selectors/products';
import { useTranslation } from '@yme/services/i18n';
import { getPLPLinkWithAccessoriesAndApparels } from '@yme/utils/product';
export function usePageNavigationData({ handleScroll, }) {
    const { pageType } = useSelector(getCommon);
    const { data: productDetailsData } = useSelector(getProductDetails);
    const requestTestRideLink = useSelector(getLinkForTestRide);
    const data = useSelector(getPageLevelNavigationData);
    const productVariationKey = useSelector(getKeyForGroupingProduct);
    const { getQueryParams } = useQueryParams({});
    const { dealerId, isIframe } = getQueryParams();
    const isUnitPDP = pageType === PDP_TYPE;
    const isClothingPDP = pageType === PDP_CLOTHING_TYPE;
    const isAccessoriesPDP = pageType === PDP_ACCESSORIES_TYPE;
    const isEDP = pageType === EDP_TYPE;
    const isTestRideCLP = pageType === TEST_RIDE_CLP_TYPE;
    const isTestRidePLP = pageType === TEST_RIDE_PLP_TYPE;
    const isClothingOrAccessoriesPDP = isClothingPDP || isAccessoriesPDP;
    const { t } = useTranslation();
    useEffect(() => {
        if (!data)
            return;
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll, data]);
    const navigationData = useMemo(() => {
        if (!data) {
            return [];
        }
        const { pageLevelNavigationItems, title } = data;
        const pageLevelNavigationItemsWithCurrent = map(pageLevelNavigationItems, (item) => ({
            ...item,
            isCurrent: item?.title === title,
        }));
        if (isUnitPDP) {
            const masterVariant = productDetailsData?.variants.find((variant) => variant.master);
            const collection = masterVariant?.attributes.find(getAttributeByName(COLLECTION_PRODUCT_ATTR))?.value;
            const navigationItemsWithQuery = pageLevelNavigationItemsWithCurrent?.map((item) => {
                const finallyLink = getPLPLinkWithAccessoriesAndApparels(item?.link, productVariationKey || productDetailsData?.key, collection);
                return {
                    ...item,
                    link: finallyLink,
                };
            });
            return Boolean(requestTestRideLink)
                ? [
                    ...navigationItemsWithQuery,
                    {
                        title: t('productCard_requestTestRideButton_label'),
                        link: requestTestRideLink,
                        isCurrent: t('productCard_requestTestRideButton_label') === title,
                    },
                ]
                : navigationItemsWithQuery;
        }
        if ((isTestRideCLP || isTestRidePLP) && dealerId) {
            return pageLevelNavigationItemsWithCurrent?.map((item) => {
                const link = getUrlWithTestRideMode({
                    url: item.link,
                    dealerId,
                    isIframe,
                });
                return {
                    ...item,
                    link,
                };
            });
        }
        return pageLevelNavigationItemsWithCurrent;
    }, [
        data,
        isUnitPDP,
        isTestRideCLP,
        isTestRidePLP,
        dealerId,
        productDetailsData?.variants,
        productDetailsData?.key,
        requestTestRideLink,
        t,
        productVariationKey,
        isIframe,
    ]);
    return {
        data,
        navigationData,
        isUnitPDP,
        isEDP,
        isClothingOrAccessoriesPDP,
    };
}
